/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { money, moneyShort, pct } from "./data";

/**
 * The three lines, drawn on the dashboard's own Recharts stack (the same
 * library every metric widget on this board already uses - see Charts.jsx).
 *
 * Revenue and Labor are filled areas - the ceiling and the floor - and
 * Expenses is drawn as a bare line, its own real value, rather than a third
 * shaded region - the boundary the other two are measured against. In "% of
 * revenue" mode Revenue would be a flat 100 line saying nothing, so it is
 * dropped and Net profit takes its place - the number the owner is actually
 * chasing once everything is a share.
 *
 * By default Labor does not start at the axis: Expenses IS its zero. Labor is
 * drawn as a BAND sitting on the Expenses line, so a $1,000 labor bill on
 * $4,000 of expenses draws between 4,000 and 5,000, and when expenses move to
 * 5,000 the whole band lifts with them. The gap left between the top of that
 * band and Revenue is then net profit, read straight off the chart, and Labor
 * can never cross Expenses because that line is its floor. Recharts does this
 * with a stack: an invisible Area carrying the Expenses value goes in first,
 * Labor stacks on it, and the stroke recharts draws for a stacked Area runs
 * along the TOP of the band only (Area.renderAreaStatically) - which is the
 * line we want. `laborBase: "zero"` opts back out to the plain reading.
 */

export const SERIES = {
  revenue: { key: "revenue", pctKey: "revenuePct", color: "#378ADD", label: "Revenue" },
  expenses: { key: "expenses", pctKey: "expensesPct", color: "#1D9E75", label: "Expenses" },
  labor: { key: "labor", pctKey: "laborPct", color: "#D85A30", label: "Labor" },
  net: { key: "net", pctKey: "netPct", color: "#7F77DD", label: "Net profit" },
  /* Not a fifth measurement - the target the Revenue line is chased against,
     which is why it is house-muted rather than a colour of its own: it has to
     read as a rule drawn on the chart, not as another thing that happened.
     No pctKey; a dollar pace has no place on a share-of-revenue axis, the
     same rule the $ goal lines already follow. */
  pace: { key: "pace", pctKey: null, color: "#667085", label: "Pace" },
  /* Not a line at all - the shaded area between Revenue and what the day
     cost, on the days revenue lost. The house red every breached goal and
     over-limit tile already uses, so "red on this card" means one thing. */
  loss: { key: "loss", pctKey: null, color: "#d92d20", label: "Loss" },
};

/* One stack, shared by the invisible Expenses base and the Labor band that
   rides on it. Any string works; it only has to match between the two. */
const LABOR_STACK = "pft-labor";

const n0 = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * Where a bucket's Labor line is PLOTTED once Expenses is its baseline —
 * expenses + labor, in whichever unit the chart is drawn in. The tooltip
 * prints it and a Labor goal line is lifted by it, so both agree with the
 * stack recharts draws rather than re-deriving it a second way.
 */
export const laborTopOf = (row, unit) => (unit === "%"
  ? n0(row?.expensesPct) + n0(row?.laborPct)
  : n0(row?.expenses) + n0(row?.labor));

/**
 * The red patch: where a bucket's revenue came in under what the bucket cost.
 *
 * Returned as the [bottom, top] pair recharts draws a range area between -
 * revenue up to total costs on a losing day. A day that DIDN'T lose returns a
 * zero-height pair at the cost line rather than null, which is what lets the
 * patch taper shut into the days either side of it instead of being chopped
 * off vertically at the last losing point. A day with no revenue at all (the
 * rest of the month) returns null, so nothing is drawn there.
 *
 * Costs is expenses + labor whether or not Labor is stacked on Expenses: it is
 * what the day actually cost, and a loss is a loss however the card chooses to
 * draw its lines.
 */
export const lossBandOf = (row, unit) => {
  const raw = unit === "%" ? row?.revenuePct : row?.revenue;
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  const revenue = Number(raw);
  const costs = laborTopOf(row, unit);
  return revenue < costs ? [revenue, costs] : [costs, costs];
};

/* The four tails, and which plotted value each one falls from. Keys are
   prefixed so they can never collide with a real series key on the row. */
const TAILS = [
  { key: "tailRevenue", id: "revenue", width: 2.4 },
  { key: "tailExpenses", id: "expenses", width: 2.4 },
  { key: "tailLabor", id: "labor", width: 2.2 },
  { key: "tailNet", id: "net", width: 2 },
];

/**
 * The run-on into the days the month hasn't reached.
 *
 * Every line stops at today, because a day that hasn't happened is null and
 * not zero (data.js) - but a stroke that simply stops mid-air, at whatever
 * height the last day left it, reads as a chart that broke rather than as a
 * month still running. So each line is given a tail: flat, at the height its
 * last real point left it, running straight across every remaining bucket
 * through the end of the window. It never drops toward the axis - a diagonal
 * dive to zero reads as the business's numbers actually falling, which isn't
 * what an unreached day means.
 *
 * Drawn faded, with no dot anywhere and no tooltip, so it reads as "this is
 * where the data stops, held flat", not as a day that earned nothing.
 *
 * Returns a copy of the rows; the row COUNT never changes, so every index the
 * chart hands out (EndLabel's `lastIndex`, a labor dot's `index`) still means
 * the same bucket.
 */
export const withTaper = (data, unit, stacked) => {
  const rows = (data || []).map((r) => ({ ...r }));
  /* The last bucket that actually happened, and the first that hasn't. */
  const lastReal = rows.reduce((acc, r, i) => (r && r.future ? acc : i), -1);
  const at = lastReal + 1;
  if (lastReal < 0 || at >= rows.length || !rows[at]?.future) return rows;
  const from = rows[lastReal];
  const own = (key) => (unit === "%" ? from[`${key}Pct`] : from[key]);
  const plotted = {
    tailRevenue: own("revenue"),
    tailExpenses: own("expenses"),
    tailNet: own("net"),
    /* Labor is plotted on top of Expenses when the card is stacked, so its
       tail has to fall from the top of the BAND - the pixel the line is
       actually drawn at - rather than from labor's own figure. */
    tailLabor: stacked ? laborTopOf(from, unit) : own("labor"),
  };
  Object.keys(plotted).forEach((key) => {
    const v = plotted[key];
    if (v == null || !Number.isFinite(Number(v))) return;
    /* Every bucket from the last real point to the end of the window gets the
       same value, so the tail line is flat rather than a diagonal into the
       first empty day. */
    for (let i = lastReal; i < rows.length; i += 1) rows[i][key] = Number(v);
  });
  return rows;
};

/** Whether any bucket in the range actually lost money - the legend only
    names the patch when there is one to point at. */
export const hasLossIn = (data, unit) => (data || []).some((row) => {
  const raw = unit === "%" ? row?.revenuePct : row?.revenue;
  return raw != null && Number.isFinite(Number(raw)) && Number(raw) < laborTopOf(row, unit);
});

/** Each line's colour to draw with — the built-in palette above, with any
    per-card override (Appearance tab) layered on top. Hex only, the same
    rule Goal Progress's own lineColor/goalColor already follow. */
export const seriesColorsOf = (settings) => {
  const overrides = settings?.seriesColors && typeof settings.seriesColors === "object" ? settings.seriesColors : {};
  return Object.keys(SERIES).reduce((out, id) => {
    const c = overrides[id];
    out[id] = typeof c === "string" && /^#[0-9a-fA-F]{6}$/.test(c) ? c : SERIES[id].color;
    return out;
  }, {});
};

/* Portaled to <body>, because the card it sits on is `.gmbw-sales-card`
   (overflow: hidden, for its own rounded corners), and a hover near the
   bottom of a short card clipped the "by department" rows right off — the
   same fix CellPopover uses for the same reason (DeptMetrics/CellPopover.jsx).
   Position is measured in two passes: render once off-screen to learn the
   tooltip's real size (it varies with how many department rows show), then
   place it against the cursor and clamp to the viewport so it can never run
   off any edge, whatever the card's own size is. */
const Tip = ({ active, payload, unit, labels, colors, showDepts, stacked, showPace, coordinate, wrapRef }) => {
  const elRef = useRef(null);
  const [box, setBox] = useState(null);
  const point = wrapRef.current && coordinate
    ? (() => {
      const r = wrapRef.current.getBoundingClientRect();
      return { x: r.left + coordinate.x, y: r.top + coordinate.y };
    })()
    : null;

  /* Deliberately no deps — this has to re-measure after EVERY render (a new
     hover can add/drop department rows and resize the tip), and the size-diff
     guard above is what stops it looping rather than a dependency array. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!elRef.current) return;
    const r = elRef.current.getBoundingClientRect();
    if (!box || Math.abs(r.width - box.w) > 0.5 || Math.abs(r.height - box.h) > 0.5) {
      setBox({ w: r.width, h: r.height });
    }
  });

  if (!active || !payload || !payload.length || !point) return null;
  const row = payload[0].payload;
  if (!row) return null;
  const fmt = (amount, share) => (unit === "%" ? pct(share) : money(amount));
  const paceRow = showPace && row.pace != null;
  const lines = [
    { id: "revenue", amount: row.revenue, share: row.revenuePct },
    { id: "expenses", amount: row.expenses, share: row.expensesPct },
    { id: "labor", amount: row.labor, share: row.laborPct },
    { id: "net", amount: row.net, share: row.netPct },
  ];
  /* Departments are the point of the hover in the sketch - who inside Labor
     is spending the share. Zero-cost rows are dropped rather than listed as
     "0%": a department with nothing on it this day is noise, not a finding. */
  const depts = (row.depts || []).filter((d) => d.cost > 0);

  const GAP = 12;
  const w = box?.w || 220;
  const h = box?.h || 160;
  let left = point.x + GAP;
  if (left + w > window.innerWidth - 8) left = point.x - w - GAP;
  left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
  const top = Math.max(8, Math.min(point.y - h / 2, window.innerHeight - h - 8));

  return createPortal(
    <div
      ref={elRef}
      className="pft-tip"
      style={{ position: "fixed", top, left, visibility: box ? "visible" : "hidden" }}
    >
      <div className="pft-tip-h">{row.full || row.label}</div>
      {/* A day the month hasn't reached yet has no figures to show and must
          not print $0 for four of them — only the target it is carrying. */}
      {row.future ? (
        <div className="pft-tip-rows">
          {paceRow && (
            <div className="pft-tip-row">
              <i style={{ background: colors.pace }} />
              <span>{labels.pace || SERIES.pace.label}</span>
              <b>{money(row.pace)}</b>
            </div>
          )}
          <div className="pft-tip-row pft-tip-stack">
            <i style={{ background: "transparent" }} />
            <span>Not here yet</span>
          </div>
        </div>
      ) : (
      <div className="pft-tip-rows">
        {lines.map((l) => (
          <div className="pft-tip-row" key={l.id}>
            <i style={{ background: colors[l.id] }} />
            <span>{labels[l.id] || SERIES[l.id].label}</span>
            <b>{fmt(l.amount, l.share)}</b>
          </div>
        ))}
        {/* Where the Labor line actually SITS when Expenses is its zero. The
            row above is still labor's own figure — this one stops the chart
            and the tooltip looking like they disagree. */}
        {stacked && (
          <div className="pft-tip-row pft-tip-stack">
            <i style={{ background: "transparent" }} />
            <span>
              {labels.expenses || SERIES.expenses.label}
              {" + "}
              {labels.labor || SERIES.labor.label}
            </span>
            <b>
              {unit === "%"
                ? pct(laborTopOf(row, "%"))
                : money(laborTopOf(row, "$"))}
            </b>
          </div>
        )}
        {paceRow && (
          <div className="pft-tip-row">
            <i style={{ background: colors.pace }} />
            <span>{labels.pace || SERIES.pace.label}</span>
            <b>{money(row.pace)}</b>
          </div>
        )}
      </div>
      )}
      {showDepts && depts.length > 0 && (
        <div className="pft-tip-depts">
          <div className="pft-tip-deptsh">{labels.labor || "Labor"} by department</div>
          {depts.map((d) => (
            <div className="pft-tip-row" key={d.key}>
              <span>{d.label}</span>
              <b>{d.pct == null ? money(d.cost) : pct(d.pct)}</b>
            </div>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
};
Tip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  unit: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  showDepts: PropTypes.bool,
  stacked: PropTypes.bool,
  showPace: PropTypes.bool,
  coordinate: PropTypes.object,
  wrapRef: PropTypes.object.isRequired,
};
Tip.defaultProps = { coordinate: null, stacked: false, showPace: false };

/* The text a ReferenceLine draws for free, for the two lines here that cannot
   BE ReferenceLines: a Labor cap lifted onto the Expenses baseline, and the
   pace line on a week/month grain - both move bucket by bucket, so both are
   real series, and a series would print its label over every point. Only the
   last is drawn, tucked in above the line's right-hand end where ReferenceLine
   puts its own. Props are prefixed so recharts' own per-point props (which are
   cloned over these) can't collide with them. */
const EndLabel = ({ index, viewBox, capText, capFill, lastIndex }) => {
  if (index !== lastIndex || !viewBox) return null;
  return (
    <text
      x={viewBox.x - 4}
      y={viewBox.y - 6}
      textAnchor="end"
      fill={capFill}
      fontSize={10}
      fontWeight={600}
    >
      {capText}
    </text>
  );
};
EndLabel.propTypes = {
  index: PropTypes.number,
  viewBox: PropTypes.object,
  capText: PropTypes.string.isRequired,
  capFill: PropTypes.string.isRequired,
  lastIndex: PropTypes.number.isRequired,
};
EndLabel.defaultProps = { index: -1, viewBox: null };

const TrendChart = ({
  data, unit, labels, colors, shown, showDepts, laborBase, showPace, showLoss, curve, grid, dots,
  goalLines, onLaborPointClick,
}) => {
  const wrapRef = useRef(null);
  const k = (id) => (unit === "%" ? SERIES[id].pctKey : SERIES[id].key);
  /* Expenses is Labor's zero unless the card opted out. Kept independent of
     `shown.expenses`: switching the Expenses LINE off hides a line, it does
     not move what Labor is measured from. */
  const stacked = laborBase !== "zero" && shown.labor;
  /* Labor's floor for a bucket, in the unit the chart is drawn in. */
  const baseOf = (row) => (unit === "%" ? n0(row?.expensesPct) : n0(row?.expenses));
  /* A pace is a figure in dollars; a share-of-revenue axis has nowhere to put
     it, the same reason a $ goal line is dropped there. */
  const drawPace = showPace && unit !== "%" && data.some((r) => r?.pace != null);
  const type = curve === "linear" ? "linear" : "monotone";
  const dot = dots ? { r: 2.4, strokeWidth: 0 } : false;
  /* Revenue as a flat 100 says nothing, so percent mode trades it for Net
     profit - the figure that actually moves once everything is a share. */
  const drawRevenue = shown.revenue && unit !== "%";
  const drawNet = shown.net || (unit === "%" && shown.revenue);

  /* Revenue has to be on the chart for "revenue fell under costs" to be a
     thing you can see; on a percent axis it isn't drawn at all, and a loss is
     already the Net profit line crossing zero. */
  const drawLoss = showLoss && drawRevenue && hasLossIn(data, unit);

  /* The rows the chart is actually handed: the same buckets, each of the four
     lines given a flat run-on across the days the month hasn't reached yet
     (withTaper). */
  const rows = withTaper(data, unit, stacked);
  const tailOn = {
    revenue: drawRevenue, expenses: shown.expenses, labor: shown.labor, net: drawNet,
  };

  /* Every point on the Labor line is clickable when there is a department to
     drill into - a bigger, invisible hit circle sits behind the visible dot
     (or in its place when dots are switched off in Appearance) so a day with
     no dot to aim at is still easy to click. Recharts only gives a whole
     series its own onClick, attached to the drawn line/area itself, not to
     one data point - a custom `dot` renderer is the one place recharts hands
     back a SINGLE point's payload. */
  const laborDot = (dotProps) => {
    const { cx, cy, payload, index } = dotProps;
    if (cx == null || cy == null) return null;
    const clickable = !!onLaborPointClick && (payload?.depts || []).some((d) => d.cost > 0);
    return (
      // eslint-disable-next-line react/no-array-index-key
      <g key={`ld${index}`} style={{ cursor: clickable ? "pointer" : "default" }}>
        <circle
          cx={cx}
          cy={cy}
          r={10}
          fill="transparent"
          onClick={clickable ? () => onLaborPointClick(payload) : undefined}
        />
        {dots && <circle cx={cx} cy={cy} r={2.4} fill={colors.labor} pointerEvents="none" />}
      </g>
    );
  };
  return (
    <div ref={wrapRef} style={{ width: "100%", height: "100%" }}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={rows} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
        {/* A soft fill under all three lines, not just Revenue's — the flat
            strokes alone read as a spreadsheet; the light wash under each one
            gives the chart the same "weight" the sketch's own shading has,
            without fighting Revenue's fill for attention (its stop is still
            the strongest of the three). */}
        <defs>
          <linearGradient id="pft-rev" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.revenue} stopOpacity="0.22" />
            <stop offset="100%" stopColor={colors.revenue} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="pft-lab" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.labor} stopOpacity="0.12" />
            <stop offset="100%" stopColor={colors.labor} stopOpacity="0" />
          </linearGradient>
          {/* The band on top of Expenses is a thin shape, and a wash that
              fades to nothing across it would leave its lower edge — the
              Expenses line, the thing it is sitting on — looking detached.
              So this one keeps a floor. */}
          {/* Deepest where the patch is deepest. The bounding box runs from the
              cost line down to the worst day's revenue, so the further under a
              day went the redder it is drawn - the size of the hole and its
              colour saying the same thing. */}
          <linearGradient id="pft-loss" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.loss} stopOpacity="0.3" />
            <stop offset="100%" stopColor={colors.loss} stopOpacity="0.55" />
          </linearGradient>
          <linearGradient id="pft-lab-band" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.labor} stopOpacity="0.2" />
            <stop offset="100%" stopColor={colors.labor} stopOpacity="0.08" />
          </linearGradient>
        </defs>
        {grid && <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--pft-grid)" />}
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          interval="preserveStartEnd"
          minTickGap={14}
          tickMargin={8}
          tick={{ fontSize: 11, fill: "var(--pft-faint)" }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={46}
          tickMargin={4}
          tick={{ fontSize: 11, fill: "var(--pft-faint)" }}
          tickFormatter={(v) => (unit === "%" ? `${Math.round(v)}%` : moneyShort(v))}
        />
        <Tooltip
          content={(
            <Tip
              unit={unit}
              labels={labels}
              colors={colors}
              showDepts={showDepts}
              stacked={stacked}
              showPace={drawPace}
              wrapRef={wrapRef}
            />
          )}
          cursor={{ stroke: "var(--pft-cursor)", strokeWidth: 1 }}
          isAnimationActive={false}
        />
        {/* Goal lines sit UNDER the data so a breach reads as the line crossing
            a limit, not as a limit drawn over the evidence. */}
        {/* A Labor cap is set against labor's OWN figure, so once labor is
            drawn from the Expenses line the flat cap would sit in a different
            frame than the line it is judging — 40% drawn at 40 while the line
            it must not cross is at expenses + 40. Lifting it by each bucket's
            own expenses puts the two back in the same frame, which makes it a
            series rather than a ReferenceLine (the lift moves point by point).
            The tiles' own Over/Close pills are unaffected either way: those
            compare labor to the cap directly and never touch the baseline. */}
        {/* Where we should be: the revenue goal divided by the month's working
            days (data.js). Straight, never curved - it is a rule, not a trend,
            and a monotone spline through identical values can still bow. It
            runs on past today because the card draws to the end of the month,
            which is what makes "we are behind with nine days left" visible. */}
        {drawPace && (
          <Line
            type="linear"
            dataKey="pace"
            name={labels.pace || SERIES.pace.label}
            stroke={colors.pace}
            strokeWidth={1.5}
            strokeDasharray="6 4"
            dot={false}
            activeDot={false}
            legendType="none"
            tooltipType="none"
            isAnimationActive={false}
            connectNulls
          >
            <LabelList
              content={(
                <EndLabel
                  capText={labels.pace || SERIES.pace.label}
                  capFill={colors.pace}
                  lastIndex={data.length - 1}
                />
              )}
            />
          </Line>
        )}
        {goalLines.filter((g) => stacked && g.id === "labor").map((g) => (
          <Line
            key={`${g.id}-cap`}
            type={type}
            dataKey={(row) => baseOf(row) + g.value}
            stroke={colors[g.id]}
            strokeWidth={1.5}
            strokeDasharray="5 4"
            strokeOpacity={0.7}
            dot={false}
            activeDot={false}
            legendType="none"
            tooltipType="none"
            isAnimationActive={false}
            connectNulls
          >
            <LabelList
              content={<EndLabel capText={g.label} capFill={colors[g.id]} lastIndex={data.length - 1} />}
            />
          </Line>
        ))}
        {goalLines.filter((g) => !(stacked && g.id === "labor")).map((g) => (
          <ReferenceLine
            key={g.id}
            y={g.value}
            stroke={colors[g.id]}
            strokeDasharray="5 4"
            strokeOpacity={0.7}
            ifOverflow="extendDomain"
            label={{
              value: g.label,
              position: "insideTopRight",
              fill: colors[g.id],
              fontSize: 10,
              fontWeight: 600,
            }}
          />
        ))}
        {drawRevenue && (
          <Area
            type={type}
            dataKey={k("revenue")}
            name={labels.revenue || SERIES.revenue.label}
            stroke={colors.revenue}
            strokeWidth={2.4}
            fill="url(#pft-rev)"
            dot={dot}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--pft-dot-ring)", fill: colors.revenue }}
            isAnimationActive={false}
            connectNulls
          />
        )}
        {/* Labor's floor. Carries the Expenses value and draws nothing at all —
            it exists only so the Labor area below stacks on top of it instead
            of on the axis. It has to be declared FIRST: recharts stacks in
            render order, and the first member of a stack is the one on the
            bottom. */}
        {stacked && (
          <Area
            type={type}
            dataKey={k("expenses")}
            stackId={LABOR_STACK}
            stroke="none"
            fill="none"
            dot={false}
            activeDot={false}
            legendType="none"
            tooltipType="none"
            isAnimationActive={false}
            connectNulls
          />
        )}
        {shown.labor && (
          <Area
            type={type}
            dataKey={k("labor")}
            stackId={stacked ? LABOR_STACK : undefined}
            name={labels.labor || SERIES.labor.label}
            stroke={colors.labor}
            strokeWidth={2.2}
            fill={stacked ? "url(#pft-lab-band)" : "url(#pft-lab)"}
            dot={onLaborPointClick ? laborDot : dot}
            activeDot={{
              r: 5,
              strokeWidth: 2,
              stroke: "var(--pft-dot-ring)",
              fill: colors.labor,
              /* Recharts draws this OVER our own dot the instant a point is
                 hovered — exactly the pixels a click is aiming at — and it
                 carries no onClick of its own, so left as-is it silently eats
                 the click. Pointer-events off lets the click fall through to
                 the invisible, always-there hit circle in laborDot(). */
              style: onLaborPointClick ? { pointerEvents: "none" } : undefined,
            }}
            isAnimationActive={false}
            connectNulls
          />
        )}
        {/* Drawn AFTER the Labor band and before every stroke. Under the band
            it was the same red in two different shades - the part of the patch
            above the expenses line came out muted by the band's orange wash
            and the part below it didn't, leaving a seam across the middle of
            the hole. On top, the patch is one colour and the Expenses and Net
            lines still draw over it. `connectNulls` off on purpose: the gaps
            are days the month hasn't reached, and bridging them would paint a
            loss across days that haven't happened. */}
        {drawLoss && (
          <Area
            type={type}
            dataKey={(row) => lossBandOf(row, unit)}
            stroke="none"
            fill="url(#pft-loss)"
            dot={false}
            activeDot={false}
            legendType="none"
            tooltipType="none"
            isAnimationActive={false}
            connectNulls={false}
          />
        )}
        {/* Expenses is drawn as a bare line, not a filled area, and rendered
            AFTER both areas so it stays visible on top of them — it is the
            boundary between Revenue's area above and Labor's area below, the
            line the other two are measured against, not a third region of its
            own. */}
        {shown.expenses && (
          <Line
            type={type}
            dataKey={k("expenses")}
            name={labels.expenses || SERIES.expenses.label}
            stroke={colors.expenses}
            strokeWidth={2.4}
            dot={dot}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--pft-dot-ring)", fill: colors.expenses }}
            isAnimationActive={false}
            connectNulls
          />
        )}
        {drawNet && (
          <Line
            type={type}
            dataKey={k("net")}
            name={labels.net || SERIES.net.label}
            stroke={colors.net}
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={dot}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--pft-dot-ring)", fill: colors.net }}
            isAnimationActive={false}
            connectNulls
          />
        )}
        {/* The flat run-on (withTaper above). Drawn last so each tail meets its
            own line's last point on top rather than under it. */}
        {TAILS
          .filter((t) => tailOn[t.id] && rows.some((r) => r[t.key] != null))
          .map((t) => (
            <Line
              key={t.key}
              className="pft-tail"
              type="linear"
              dataKey={t.key}
              stroke={colors[t.id]}
              strokeWidth={t.width}
              strokeOpacity={0.28}
              dot={false}
              activeDot={false}
              legendType="none"
              tooltipType="none"
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
      </ComposedChart>
    </ResponsiveContainer>
    </div>
  );
};

TrendChart.propTypes = {
  data: PropTypes.array.isRequired,
  /** "$" or "%" - which set of keys the lines read. */
  unit: PropTypes.string.isRequired,
  /** Per-card names for the four series (Setup's rename fields). */
  labels: PropTypes.object.isRequired,
  /** Per-card colours for the four series (Appearance's colour swatches), keyed the same way. */
  colors: PropTypes.object.isRequired,
  shown: PropTypes.object.isRequired,
  showDepts: PropTypes.bool,
  /** "expenses" (default) draws Labor up from the Expenses line; "zero" from the axis. */
  laborBase: PropTypes.string,
  /** Draw the pace line, where the rows carry a `pace` and the axis is in dollars. */
  showPace: PropTypes.bool,
  /** Shade the gap between Revenue and costs on the buckets that lost money. */
  showLoss: PropTypes.bool,
  curve: PropTypes.string,
  grid: PropTypes.bool,
  dots: PropTypes.bool,
  goalLines: PropTypes.array,
  /** Called with a bucket's full data row when its Labor point is clicked. */
  onLaborPointClick: PropTypes.func,
};
TrendChart.defaultProps = {
  showDepts: true,
  laborBase: "expenses",
  showPace: false,
  showLoss: true,
  curve: "monotone",
  grid: true,
  dots: true,
  onLaborPointClick: null,
  goalLines: [],
};

export default TrendChart;
